import { useDispatch } from '@redux';
import { getAllOrderHistories } from '@redux/orderHistories/actions';
import { confirmOrder, getByIdOrders } from '@redux/orders/actions';
import { Button, Modal } from 'antd';
import { every, flatten, some } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DynamicObjectType } from 'types/commons';

interface IConfirmModalProps {
  data: any;
  visible: boolean;
  onCancel: () => void;
}

const ConfirmModal = ({ data, visible, onCancel }: IConfirmModalProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [confirmLoading, setConfirmLoading] = useState(false);

  const CHECK_LIST = [
    {
      text: 'orders.enoughStock',
      key: 'enoughStock',
      isPassed:
        every(
          data?.items?.filter((item) => !item.isCombo),
          (item) =>
            some(
              item?.stocks?.filter((stock) => stock.id === data?.locationId),
              (store) => store.stock >= item.quantity,
            ),
        ) &&
        (data?.items?.filter((item) => item.isCombo)?.length === 0 ||
          every(
            flatten(
              data?.items
                ?.filter((item) => item.isCombo)
                ?.map(
                  (item) =>
                    item?.product?.comboItems?.map((comboItem) => ({
                      ...comboItem,
                      comboQuantity: item?.quantity,
                    })) || [],
                ),
            ),
            (item: DynamicObjectType) =>
              some(
                item?.stocks?.filter((stock) => stock.id === data?.locationId),
                (store) =>
                  store.stock >= +item?.quantity * (+item?.comboQuantity || 1),
              ),
          )),
    },
    ...(data?.isMarketPlace
      ? [
          {
            text: 'orders.isMarketPlaceConfirmed',
            key: 'isMarketPlaceConfirmed',
            isPassed: data?.isMarketPlaceConfirmed,
          },
        ]
      : [
          {
            text:
              data?.payments?.find((e) => e.type === 'cod') ||
              data?.paymentType === 'cod'
                ? 'orders.confirmedCOD'
                : 'orders.confirmedPaid',
            key: 'confirmedOrPaid',
            isPassed: (() => {
              if (
                data?.payments?.find((e) => e.type === 'cod') ||
                data?.paymentType === 'cod'
              ) {
                return data?.isConfirmed;
              }
              return data?.payments?.length > 0
                ? every(data?.payments, (e) => e.status === 'paid')
                : data?.paymentStatus === 'paid';
            })(),
          },
        ]),
    ...(data?.payments?.find((e) => e.type === 'cod') ||
    data?.paymentType === 'cod'
      ? [
          {
            text: 'orders.lessThan1halfMil',
            key: 'lessThan1halfMil',
            isPassed: Number(data?.total) < 1500000,
          },
        ]
      : []),
  ];

  const onSubmitConfirmOrder = async (values) => {
    try {
      setConfirmLoading(true);
      await dispatch(
        confirmOrder({
          data: {
            id: data?.id,
            data: values,
          },
          options: {},
        }),
      );
      await dispatch(
        getByIdOrders({
          data: {
            id: data?.id,
          },
          options: { isRequestApi: true, isRefresh: true },
        }),
      );
      await dispatch(
        getAllOrderHistories({
          data: {
            pageSize: 100,
            join: ['creator'],
            filters: [
              {
                condition: 'and',
                key: 'orderId',
                value: data?.id,
                operator: 'eq',
              },
            ],
            orderBy: '-createdAt',
          },
          options: {
            isRefresh: true,
          },
        }),
      );
      setConfirmLoading(false);
      onCancel();
    } catch (error) {
      setConfirmLoading(false);
      onCancel();
    }
  };

  return (
    <Modal title="Confirm Order" open={visible} footer={null}>
      <div>
        {!CHECK_LIST[0]?.isPassed ? (
          <div className="txt-error">{t('orderDetail.warningOutOffStock')}</div>
        ) : (
          ''
        )}
      </div>
      <div className="flex items-center gap-1 justify-end">
        <Button onClick={onCancel}>{t('button.cancel')}</Button>
        <Button
          type="primary"
          className="bg-[--primary]"
          onClick={() =>
            onSubmitConfirmOrder({
              storeId: data?.locationId,
              partnerId: data?.partnerId,
            })
          }
          loading={confirmLoading}
        >
          {t('button.ok')}
        </Button>
      </div>
    </Modal>
  );
};

export default ConfirmModal;
